/** これを下回ると在庫不足（アサインできる案件が枯れかけ）として警告色にする。 @graph-connects none */
export const SUPPLY_RATE_WARN_THRESHOLD = 100;

/** バーの最大表示率。100% を超えても振り切れないよう頭打ちにする。 @graph-connects none */
const BAR_MAX_PERCENT = 100;

export interface SupplyRateStackedBarProps {
  /** 要件定義アサイン可能（担当者なし）分の割合（%） */
  requirementAssignablePercent: number;
  /** 要件定義着手予定（担当者あり・未着手）分の割合（%） */
  requirementPlannedStartPercent: number;
  /** 開発アサイン可能分の割合（%） */
  assignablePercent: number;
  /** 開発着手予定（アサイン済み・未着手）分の割合（%） */
  plannedStartPercent: number;
  /** バー全体の aria-label */
  label: string;
}

/**
 * 4 段を積み上げた横棒バー。全幅 = 先 2 週間分の予算（分母 100%）で、
 * 左から 要件定義アサイン可能（淡いティール）→ 要件定義着手予定（濃いティール）→
 * 開発アサイン可能（スカイ）→ 開発着手予定（濃いブルー）の順に積む。
 *
 * 4 段は漏斗の関係（要件定義 → 開発）にあるので、別々のバーではなく 1 本に積む。
 * こうすると「合計でも枠を埋められていない」ことが余白の長さでそのまま読める。
 * 並び順はカードの段表示と揃える。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business PMO充足率の積み上げバー。分母を全幅として、要件定義アサイン可能・要件定義着手予定・開発アサイン可能・開発着手予定の4段を左から順に2pxの地色の隙間を挟んで積み上げ、残りの余白で不足量を示す
 * @graph-connects none
 */
export function SupplyRateStackedBar({
  requirementAssignablePercent,
  requirementPlannedStartPercent,
  assignablePercent,
  plannedStartPercent,
  label,
}: SupplyRateStackedBarProps) {
  // 合計が 100% を超えても溢れないよう、カードの並び順で先に来た段から幅を確保し、
  // 残り幅に次の段を収める
  const reqAssignableWidth = Math.min(requirementAssignablePercent, BAR_MAX_PERCENT);
  const reqPlannedWidth = Math.min(
    requirementPlannedStartPercent,
    BAR_MAX_PERCENT - reqAssignableWidth,
  );
  const assignableWidth = Math.min(
    assignablePercent,
    BAR_MAX_PERCENT - reqAssignableWidth - reqPlannedWidth,
  );
  const plannedStartWidth = Math.min(
    plannedStartPercent,
    BAR_MAX_PERCENT - reqAssignableWidth - reqPlannedWidth - assignableWidth,
  );

  // 幅 0 の段にも gap が乗ると地色の隙間だけが並ぶため、描画前に落とす
  const segments = [
    { width: reqAssignableWidth, color: '--color-supply-req-assignable' },
    { width: reqPlannedWidth, color: '--color-supply-req-planned' },
    { width: assignableWidth, color: '--color-supply-dev-assignable' },
    { width: plannedStartWidth, color: '--color-supply-dev-planned' },
  ].filter((segment) => segment.width > 0);

  return (
    // gap-[2px]: 似た色相（要件定義のティール 2 段 / 開発のブルー 2 段）が隣り合うため、
    // 段の境目にバーの地色を 2px 覗かせて境界を作る
    <div
      className="flex h-2.5 w-full gap-[2px] overflow-hidden rounded-full bg-[var(--color-bg-elevated)]"
      role="img"
      aria-label={label}
    >
      {segments.map((segment) => (
        <div
          key={segment.color}
          className="h-full"
          style={{ width: `${String(segment.width)}%`, backgroundColor: `var(${segment.color})` }}
        />
      ))}
    </div>
  );
}

export interface SupplyRateStageProps {
  /** 段の名前 */
  label: string;
  /** 充足率（%）。算出不可なら null */
  rate: number | null;
  /** 率の前に置く演算子（`+` / `=`）。先頭の段では省略する */
  operator?: string;
  /** 凡例ドットの色（CSS 変数名）。合計列など色を持たない段では省略する */
  dotColor?: string;
  /** pt や件数などの補足 */
  hint: string;
  /** 指定すると段全体がボタンになりドリルダウンを開く */
  onClick?: () => void;
}

/**
 * 漏斗 1 段分の表示（演算子 + 率 + 段名 + 補足）。
 * 「要件定義 2 段 ＋ 開発 2 段 ＝ 合計」を横一列に並べ、足し算関係をそのまま見せる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business PMO充足率カードの段表示。演算子・充足率・段名・pt/件数を縦に積み、onClick 指定時はドリルダウン用ボタンとして描画する
 * @graph-connects none
 */
export function SupplyRateStage({
  label,
  rate,
  operator,
  dotColor,
  hint,
  onClick,
}: SupplyRateStageProps) {
  const body = (
    <>
      <div className="flex items-center gap-1.5">
        {dotColor && (
          <span
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: `var(${dotColor})` }}
          />
        )}
        <span className="text-xs text-[var(--color-text-muted)]">{label}</span>
      </div>
      <p className="mt-1 flex items-baseline gap-1">
        {operator && (
          <span className="font-mono text-xl text-[var(--color-text-muted)]">{operator}</span>
        )}
        <span className="font-mono text-3xl font-bold tabular-nums text-[var(--color-text-primary)]">
          {rate !== null ? `${String(rate)}%` : '-'}
        </span>
      </p>
      <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">{hint}</p>
    </>
  );

  if (!onClick) {
    return <div className="px-1 py-0.5">{body}</div>;
  }
  // button は既定で中身を縦中央に寄せるため、グリッドで引き伸ばされた段のうち補足が
  // 1 行しかないものだけ中身が下へ落ちて見える。縦フレックスの上揃えに固定して、
  // 補足の行数に関わらず全段の見出しと数値の高さを揃える
  return (
    <button
      type="button"
      onClick={onClick}
      className="-mx-1 flex flex-col items-start justify-start rounded-lg px-2 py-0.5 text-left transition-colors hover:bg-[var(--color-bg-hover)]"
    >
      {body}
    </button>
  );
}
